import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomLocationState } from 'react-router-dom';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';
import { compose } from 'redux';
import { reduxForm, getFormValues, formValueSelector, getFormSubmitErrors, getFormSyncErrors } from 'redux-form';
import type { FormErrors, FormErrorsWithArray, InjectedFormProps } from 'redux-form';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { isValidTextInput } from '@acceligentllc/shared/utils/text';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkRequestStatus } from '@acceligentllc/shared/enums/workRequestStatus';

import { EMAIL_REGEX } from '@acceligentllc/shared/constants/regex';

import type { RootState } from 'af-reducers';

import { useToggle } from 'af-utils/react.util';

import { BillingCodeEditMode } from 'ab-enums/billingCodeEditMode.enum';
import PagePermissions from 'ab-enums/pagePermissions.enum';

import * as FORMS from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import { Button } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ColorSquare from 'af-components/ColorSquare';
import ConfirmationModal from 'af-components/ConfirmationModal';
import type { BillingCodeFM } from 'af-components/SharedForms/Job/BillingCodes/BillingCodes/BillingCodeArray';
import type { SubjobFM } from 'af-components/SharedForms/Job/formModel';
import JobUpsertFM from 'af-components/SharedForms/Job/formModel';
import SubmitButton from 'af-components/SubmitButton';
import TabNavigation from 'af-components/TabNavigation';

import * as AttachmentActions from 'af-actions/attachment';
import * as JobActions from 'af-actions/jobs';
import * as WorkRequestActions from 'af-actions/workRequests';

import CopyModal from '../../Shared/CopyModal';
import { TABS } from '../helpers';
import styles from '../styles.module.scss';
import type { FormJobOutletContext } from '../types';
import { JobEditTabRoutes } from './types';

interface LocationState extends CustomLocationState {
	subjob?: SubjobFM;
	previousFormState?: JobUpsertFM;
}

type FormProps = InjectedFormProps<JobUpsertFM, FormOwnProps>;

type FormOwnProps = ConnectedProps<typeof connector>;
type Props = FormOwnProps & FormProps;

interface BillingCodeChangePreview {
	before: BillingCodeFM;
	after: BillingCodeFM;
}

const _isInvalidBillingCode = (billingCodeError) => {
	return Object.keys(billingCodeError).length > 0;
};

const _isInvalidBillingCodeGroup = (billingCodeGroupsError) => {
	return Object.keys(billingCodeGroupsError).length > 0;
};

const billingCodeChangedFields: (keyof BillingCodeFM)[] = [
	'lineItemNumber',
	'customerNumber',
	'customerId',
	'ownerNumber',
	'ownerId',
	'unit',
	'unitPrice',
	'bidQuantity',
	'groupId',
	'groupKey',
	'group',
	'groupColor',
	'description',
];

function normalizeNullableValue(value: unknown): string {
	if (value === null || value === undefined) {
		return '';
	}

	if (value instanceof Date) {
		return value.toISOString();
	}

	return `${value}`;
}

function hasRelevantBillingCodeChanges(before: BillingCodeFM, after: BillingCodeFM): boolean {
	for (const field of billingCodeChangedFields) {
		if (normalizeNullableValue(before[field]) !== normalizeNullableValue(after[field])) {
			return true;
		}
	}

	return false;
}

function getBillingCodeChangePreviews(originalCodes: BillingCodeFM[], nextCodes: BillingCodeFM[]): BillingCodeChangePreview[] {
	const originalByDatabaseId = originalCodes.reduce<Record<number, BillingCodeFM>>((acc, billingCode) => {
		if (billingCode.databaseId && !billingCode.deletedAt) {
			acc[billingCode.databaseId] = billingCode;
		}

		return acc;
	}, {});

	const previews: BillingCodeChangePreview[] = [];

	for (const nextCode of nextCodes ?? []) {
		if (!nextCode.databaseId) {
			continue;
		}

		if (nextCode.deletedAt) {
			const before = originalByDatabaseId[nextCode.databaseId];

			if (before) {
				previews.push({ before, after: nextCode });
			}

			continue;
		}

		if (!nextCode.editMode) {
			continue;
		}

		const before = originalByDatabaseId[nextCode.databaseId];

		if (!before) {
			continue;
		}

		if (!hasRelevantBillingCodeChanges(before, nextCode)) {
			continue;
		}

		previews.push({ before, after: nextCode });
	}

	return previews;
}

function formatBillingCodeDate(value: Nullable<Date | string>): string {
	if (!value) {
		return '-';
	}

	return TimeUtils.formatDate(value, TimeFormat.DATE_ONLY);
}

function formatUnitPrice(value: Nullable<string>): string {
	if (!value) {
		return '-';
	}

	const numericValue = Number(value);

	if (Number.isNaN(numericValue)) {
		return value;
	}

	return `$${numericValue.toFixed(2)}`;
}

const EditJob: React.FC<Props> = (props) => {

	const {
		companyName,
		handleSubmit,
		submitting,
		editJob,
		getJobForm,
		initialize,
		change,
		jobHazardAssessmentStatus,
		isProject,
		subjobs,
		projectId,
		currentFormValues,
		deletePendingAttachments,
		uploadedAttachmentIds,
		formJobCode,
		title,
		customerCompanyName,
		priority,
		office,
		jobCode,
		syncErrors,
		billToEmail,
		jobCodeSubmitError,
		findAllAvailableSubjobs,
		initialized,
		hasPermissionsToManageBillingCodes,
		billingCodesSubmitErrors,
		hasCreatePermission,
		invoiceReminderRecipients,
		workLocation,
	} = props;

	const { state } = useLocation();
	const { defaultDueDate = new Date(), redirectUrl, orgAlias, originUrl, previousFormState, subjob } = state as LocationState;
	const params = useParams();
	const navigate = useNavigate();
	const { jobId, '*': tabId } = params;

	const [pendingBillingCodeSubmitForm, setPendingBillingCodeSubmitForm] = React.useState<Nullable<JobUpsertFM>>(null);
	const [billingCodeChangePreviews, setBillingCodeChangePreviews] = React.useState<BillingCodeChangePreview[]>([]);
	const [showBillingCodeChangesModal, setShowBillingCodeChangesModal] = React.useState(false);

	const dueDate = React.useMemo(() =>
		props.dueDate ?? TimeUtils.formatDate(defaultDueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.JS_TIME),
		[defaultDueDate, props.dueDate]);

	const activeTabId = React.useMemo(() => {
		switch (tabId) {
			case JobEditTabRoutes.DETAILS:
				return TABS[0].id;
			case JobEditTabRoutes.JOB_HAZARD_ASSESSMENT:
				return TABS[1].id;
			case JobEditTabRoutes.BILLING_CODES:
				return TABS[2].id;
			case JobEditTabRoutes.ATTACHMENTS:
				return TABS[3].id;
			default:
				return TABS[0].id;
		}
	}, [tabId]);
	const cameFromPreviewTab = React.useRef<string | undefined>(undefined);

	const [showCopyModal, setShowCopyModal] = React.useState(false);
	const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = React.useState(false);
	const [job, setJob] = React.useState<Nullable<JobUpsertFM>>();
	const [availableSubjobs, setAvailableSubjobs] = React.useState<SubjobFM[]>([]);
	const [selectedSubjob, setSelectedSubjob] = React.useState<Nullable<SubjobFM>>(null);
	const [subjobSearchText, setSubjobSearchText] = React.useState<string>('');

	const {
		value: showTurnToProjectConfirmationModal,
		setToTrue: openTurnToProjectConfirmationModal,
		setToFalse: hideTurnToProjectConfirmationModal,
	} = useToggle(false);

	const {
		value: showAddSubjobConfirmationModal,
		setToTrue: openAddSubjobConfirmationModal,
		setToFalse: hideAddSubjobConfirmationModal,
	} = useToggle(false);

	const {
		value: showSubmitWithSubjobsConfirmationModal,
		setToTrue: openSubmitWithSubjobsConfirmationModal,
		setToFalse: hideSubmitWithSubjobsConfirmationModal,
	} = useToggle(false);

	const {
		value: hasAddedSujobs,
		setToTrue: setAddedSujobs,
	} = useToggle(false);

	const {
		value: turnToProjectDisabled,
		setToTrue: disableTurnToProject,
	} = useToggle(isProject);

	const uploadedAttachmentIdsToDeleteRef = React.useRef<Nullable<number[]>>(null);

	const fetchAndInitializeData = React.useCallback(async () => {
		if (initialized) {
			return;
		}
		cameFromPreviewTab.current = tabId;
		const fetchedJob = await getJobForm(+jobId!);
		if (!fetchedJob) {
			throw new Error('Job not found');
		}

		const form = JobUpsertFM.fromFindJobVM(fetchedJob);

		setJob(form);
		if (fetchedJob.status === WorkRequestStatus.FINISHED) {
			navigate(originUrl || CLIENT.COMPANY.JOBS.TABLE(orgAlias, companyName));
		} else {
			initialize(form);
		}

		if (previousFormState) {
			// Directly handle the special case for 'subjobs'
			if (previousFormState.hasOwnProperty('subjobs')) {
				const updatedSubjobs = subjob ? [...(previousFormState.subjobs ?? []), subjob] : previousFormState.subjobs;
				change('subjobs', updatedSubjobs);
			}

			// Iterate over and update the rest of the form state
			Object.entries(previousFormState).forEach(([key, value]) => {
				if (key !== 'subjobs') { // Skip 'subjobs' since it's already handled
					change(key, value);
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyName, getJobForm, orgAlias, originUrl, initialized]);

	React.useEffect(() => {
		fetchAndInitializeData();
	}, [fetchAndInitializeData]);

	const clearPendingAttachments = React.useCallback(async () => {
		if (uploadedAttachmentIdsToDeleteRef.current?.length) {
			await deletePendingAttachments({ attachmentIds: uploadedAttachmentIdsToDeleteRef.current });
		}
	}, [deletePendingAttachments]);

	React.useEffect(() => {
		return () => {
			clearPendingAttachments();
		};
	}, [clearPendingAttachments]);

	React.useEffect(() => {
		uploadedAttachmentIdsToDeleteRef.current = uploadedAttachmentIds ?? null;
	}, [uploadedAttachmentIds]);

	const submit = React.useCallback(async (form: JobUpsertFM) => {
		if (!job) {
			return;
		}

		const changedFields: Partial<JobUpsertFM> = {};
		for (const _key of Object.keys(job)) {
			if (_key === 'isProject') {
				changedFields[_key] = form[_key];
				continue;
			}
			if (job[_key] !== form[_key]) {
				changedFields[_key] = form[_key];
				if (_key === 'workRequestAttachments') {
					changedFields.directories = form.directories;
					changedFields.uploadedAttachmentIds = form.uploadedAttachmentIds;
				}
				if (_key === 'directories') {
					changedFields.workRequestAttachments = form.workRequestAttachments;
				}
			}
		}

		const rm = JobUpsertFM.toRM(changedFields);

		await editJob(rm, +jobId!);
		navigate(originUrl || CLIENT.COMPANY.JOBS.PREVIEW(orgAlias, companyName, jobId));
	}, [companyName, editJob, job, jobId, navigate, orgAlias, originUrl]);

	const closeTurnToProjectConfirmationModal = React.useCallback(async () => {
		change('isProject', false);
		hideTurnToProjectConfirmationModal();
	}, [change, hideTurnToProjectConfirmationModal]);

	const closeAddSubjobConfirmationModal = React.useCallback(async () => {
		hideAddSubjobConfirmationModal();
	}, [hideAddSubjobConfirmationModal]);

	const addSubjob = React.useCallback(() => {
		setAddedSujobs();
		if (selectedSubjob) {
			change('subjobs', [...(subjobs ?? []), selectedSubjob]);
			setAvailableSubjobs([...availableSubjobs.filter((_sj) => _sj.id !== selectedSubjob.id)]);
			setSelectedSubjob(null);
		}
	}, [availableSubjobs, change, selectedSubjob, setAddedSujobs, subjobs]);

	const goToJobForm = React.useCallback(() => {
		// redirects user to job form. when user fills the form and save it, he/she will be
		// redirected back to original job form with new subjob and all other changes
		navigate(CLIENT.COMPANY.JOBS.CREATE(orgAlias, companyName), {
			state: {
				redirectUrl: CLIENT.COMPANY.JOBS.EDIT(orgAlias, companyName, jobId),
				defaultDueDate: TimeUtils.parseDate(dueDate),
				orgAlias,
				jobCode: subjobSearchText,
				previousFormState: currentFormValues,
			},
		});
	}, [companyName, currentFormValues, dueDate, jobId, navigate, orgAlias, subjobSearchText]);

	const handleSubjobSelect = React.useCallback((selectedOption: Nullable<SubjobFM>) => {
		setSelectedSubjob(selectedOption);
	}, []);

	const onTurnToProjectChange = React.useCallback((value: boolean) => {
		if (value) {
			openTurnToProjectConfirmationModal();
		}
	}, [openTurnToProjectConfirmationModal]);

	const onEditJob = React.useCallback(async (form: JobUpsertFM) => {
		if (!job) {
			return;
		}

		const _form = { ...form, billingCodeGroups: form.billingCodeGroups.filter((bcg) => !bcg.deletedAt) };

		if (!_form?.isInternal && job.isInternal) {
			setShowSubmitConfirmationModal(true);
		} else {
			await submit(_form);
		}
	}, [job, submit]);

	const beforeEditJob = React.useCallback(async (form: JobUpsertFM) => {
		if (!job) {
			return;
		}

		const previews = getBillingCodeChangePreviews(job.billingCodes ?? [], form.billingCodes ?? []);

		if (previews.length) {
			setBillingCodeChangePreviews(previews);
			setPendingBillingCodeSubmitForm(form);
			setShowBillingCodeChangesModal(true);
			return;
		}

		if (!hasAddedSujobs) {
			await onEditJob(form);
			return;
		}

		openSubmitWithSubjobsConfirmationModal();
	}, [hasAddedSujobs, job, onEditJob, openSubmitWithSubjobsConfirmationModal]);

	const closeBillingCodeChangesModal = React.useCallback(() => {
		setShowBillingCodeChangesModal(false);
		setPendingBillingCodeSubmitForm(null);
		setBillingCodeChangePreviews([]);
	}, []);

	const confirmBillingCodeChanges = React.useCallback(async () => {
		if (!pendingBillingCodeSubmitForm) {
			closeBillingCodeChangesModal();
			return;
		}

		const form = pendingBillingCodeSubmitForm;

		setShowBillingCodeChangesModal(false);
		setPendingBillingCodeSubmitForm(null);
		setBillingCodeChangePreviews([]);

		if (!hasAddedSujobs) {
			await onEditJob(form);
			return;
		}

		openSubmitWithSubjobsConfirmationModal();
	}, [
		closeBillingCodeChangesModal,
		hasAddedSujobs,
		onEditJob,
		openSubmitWithSubjobsConfirmationModal,
		pendingBillingCodeSubmitForm,
	]);

	const onBack = React.useCallback(() => {
		const goToPreviewTab = cameFromPreviewTab.current
			? CLIENT.COMPANY.JOBS.PREVIEW_TAB(orgAlias, companyName, jobId, cameFromPreviewTab.current)
			: CLIENT.COMPANY.JOBS.PREVIEW(orgAlias, companyName, jobId);
		navigate(originUrl || goToPreviewTab);
	}, [companyName, jobId, navigate, orgAlias, originUrl]);

	const onTabSelect = React.useCallback((newTabId: number) => {
		let tabName = '';
		switch (newTabId) {
			case TABS[0].id:
				tabName = JobEditTabRoutes.DETAILS;
				break;
			case TABS[1].id:
				tabName = JobEditTabRoutes.JOB_HAZARD_ASSESSMENT;
				break;
			case TABS[2].id:
				tabName = JobEditTabRoutes.BILLING_CODES;
				break;
			case TABS[3].id:
				tabName = JobEditTabRoutes.ATTACHMENTS;
				break;
			default:
				tabName = JobEditTabRoutes.DETAILS;
				break;
		}
		navigate(CLIENT.COMPANY.JOBS.EDIT_TAB(orgAlias, companyName, jobId, tabName));
	}, [companyName, jobId, navigate, orgAlias]);

	const openCopyJobModal = React.useCallback(() => {
		setShowCopyModal(true);
	}, []);

	const closeCopyJobModal = React.useCallback(() => {
		setShowCopyModal(false);
	}, []);

	const closeConfirmationModal = React.useCallback(() => {
		setShowSubmitConfirmationModal(false);
	}, []);

	const isBillToEmailValid = billToEmail ? EMAIL_REGEX.test(billToEmail) : true;

	const areJobsFieldsValid = React.useMemo(() => {
		return (
			!!formJobCode
			&& isBillToEmailValid
			&& !!title
			&& !!customerCompanyName
			&& !!priority
			&& !!office
			&& !jobCodeSubmitError
		);
	}, [formJobCode, isBillToEmailValid, title, customerCompanyName, priority, office, jobCodeSubmitError]);

	const tabsAdjustedForPermissions = React.useMemo(() => {
		const adjustedTabs: typeof TABS = [];
		for (const _tab of TABS) {
			if (!hasPermissionsToManageBillingCodes && _tab.id === TABS[2].id) {
				continue;
			}
			adjustedTabs.push(_tab);
		}
		return adjustedTabs;
	}, [hasPermissionsToManageBillingCodes]);

	const areBillingCodesValid = React.useMemo(() => !syncErrors?.billingCodes?.some(_isInvalidBillingCode), [syncErrors]);

	const renderTabLabel = React.useCallback((id: number) => {
		const areRequiredHazardAssessmentFieldsFilled = !!jobHazardAssessmentStatus;
		switch (id) {
			case TABS[0].id: {
				return (
					<div className={styles['job-form__tab']}>
						{!areJobsFieldsValid && <span className="icon-warning" />}
						Job
					</div>
				) as React.JSX.Element;
			}
			case TABS[1].id: {
				return (
					<div className={styles['job-form__tab']}>
						{!areRequiredHazardAssessmentFieldsFilled && <span className="icon-warning" />}
						Job Hazard Assessment
					</div>
				) as React.JSX.Element;
			}

			case TABS[2].id: {
				return (
					<div className={styles['job-form__tab']}>
						{!areBillingCodesValid && <span className="icon-warning" />}
						Billing Codes
					</div>
				) as React.JSX.Element;
			}
			case TABS[3].id: {
				return (
					<div className={styles['job-form__tab']}>
						Attachments
					</div>
				) as React.JSX.Element;
			}

			default: {
				return <></>;
			}
		}
	}, [jobHazardAssessmentStatus, areJobsFieldsValid, areBillingCodesValid]);

	const renderBillingCodeDescriptionItem = React.useCallback(
		(item: React.ReactNode, index: number) => (
			<React.Fragment key={index}>
				{index > 0 && <span className={styles['billing-code-change-modal__separator']}> • </span>}
				{item}
			</React.Fragment>
		),
		[]
	);

	const renderBillingCodeGroup = React.useCallback((billingCode: BillingCodeFM) => {
		if (!billingCode.group) {
			return null;
		}

		return (
			<span className={styles['billing-code-change-modal__group']}>
				{billingCode.groupColor && <ColorSquare color={billingCode.groupColor} />}
				<span>{billingCode.group}</span>
			</span>
		);
	}, []);

	const renderBillingCodeInlineDescription = React.useCallback((billingCode: BillingCodeFM) => {
		const items: React.ReactNode[] = [
			billingCode.customerId || null,
			billingCode.unit ?? null,
			billingCode.unitPrice ? formatUnitPrice(billingCode.unitPrice) : null,
			renderBillingCodeGroup(billingCode),
			billingCode.description || null,
			<div key="active-since">Active since {formatBillingCodeDate(billingCode.createdAt)}</div>,
		].filter(Boolean);

		return (
			<div className={styles['billing-code-change-modal__billing-code']}>
				{items.map(renderBillingCodeDescriptionItem)}
			</div>
		);
	}, [
		renderBillingCodeDescriptionItem,
		renderBillingCodeGroup,
	]);

	const editedAndRecalculatedPreviews = React.useMemo(() => {
		return billingCodeChangePreviews.filter((preview) => (
			!preview.after.deletedAt
			&& preview.after.editMode === BillingCodeEditMode.DIRECT_EDIT
		));
	}, [billingCodeChangePreviews]);

	const archivedAndCreatedPreviews = React.useMemo(() => {
		return billingCodeChangePreviews.filter((preview) => (
			preview.after.editMode === BillingCodeEditMode.ARCHIVE_AND_EDIT
		));
	}, [billingCodeChangePreviews]);

	const archivedPreviews = React.useMemo(() => {
		return billingCodeChangePreviews.filter((preview) => (
			preview.after.deletedAt
			&& preview.after.editMode !== BillingCodeEditMode.ARCHIVE_AND_EDIT
		));
	}, [billingCodeChangePreviews]);

	const renderBillingCodeChangeSection = React.useCallback((
		_title: string,
		previews: BillingCodeChangePreview[]
	) => {
		if (!previews.length) {
			return null;
		}

		return (
			<div className={styles['billing-code-change-modal__section']}>
				<div className={styles['billing-code-change-modal__section-title']}>
					{_title}
				</div>

				<div className={styles['billing-code-change-modal__section-list']}>
					{previews.map((preview) => (
						<div
							className={styles['billing-code-change-modal__row']}
							key={preview.before.databaseId ?? preview.before.id}
						>
							{renderBillingCodeInlineDescription(preview.before)}
						</div>
					))}
				</div>
			</div>
		);
	}, [renderBillingCodeInlineDescription]);

	const renderBillingCodeChangesModalBody = React.useCallback(() => {
		return (
			<div className={styles['billing-code-change-modal']}>
				<div className={styles['billing-code-change-modal__intro']}>
					Review the changes below before applying. This will update all job work sheets where this code was used.
				</div>

				<div className={styles['billing-code-change-modal__content']}>
					{renderBillingCodeChangeSection(
						'Edited & Recalculated Billing Code:',
						editedAndRecalculatedPreviews
					)}

					{renderBillingCodeChangeSection(
						'Archived and Created New Billing Code:',
						archivedAndCreatedPreviews
					)}

					{renderBillingCodeChangeSection(
						'Archived Billing Code:',
						archivedPreviews
					)}
				</div>
			</div>
		);
	}, [
		archivedAndCreatedPreviews,
		archivedPreviews,
		editedAndRecalculatedPreviews,
		renderBillingCodeChangeSection,
	]);

	React.useEffect(() => {
		if (isProject && !turnToProjectDisabled) {
			disableTurnToProject();
		}
	}, [disableTurnToProject, isProject, turnToProjectDisabled]);

	const fetchAvailableSubjobs = React.useCallback(async () => {
		const _subjobs = await findAllAvailableSubjobs(+jobId!);
		const subjobIds: number[] = subjobs?.map((_s) => _s.id) ?? [];
		if (!!jobId) {
			subjobIds.push(+jobId);
		}
		setAvailableSubjobs(_subjobs.filter((_sj) => !subjobIds.includes(_sj.id)));
	}, [findAllAvailableSubjobs, jobId, subjobs]);

	React.useEffect(() => {
		if (initialized && job?.isProject) {
			fetchAvailableSubjobs();
		}
	}, [fetchAvailableSubjobs, initialized, job?.isProject]);

	const turnToProject = React.useCallback(() => {
		change('isProject', true);
		fetchAvailableSubjobs();
	}, [change, fetchAvailableSubjobs]);

	const isFormValid = React.useMemo(() => (
		isValidTextInput(formJobCode)
		&& isValidTextInput(title)
		&& isValidTextInput(customerCompanyName)
		&& !!priority
		&& !!office
		&& !!jobHazardAssessmentStatus
		&& isBillToEmailValid
		&& !syncErrors?.billingCodes?.some(_isInvalidBillingCode)
		&& !syncErrors?.billingCodeGroups?.some(_isInvalidBillingCodeGroup)
	), [
		formJobCode,
		title,
		customerCompanyName,
		priority,
		office,
		jobHazardAssessmentStatus,
		isBillToEmailValid,
		syncErrors?.billingCodes,
		syncErrors?.billingCodeGroups,
	]);

	const context = React.useMemo<FormJobOutletContext>(() => ({
		availableSubjobs,
		change,
		disableTurnToProject,
		formName: FORMS.JOB_EDIT,
		goToJobForm,
		handleSubjobSelect,
		isProject,
		onTurnToProjectChange,
		openAddSubjobConfirmationModal,
		orgAlias,
		projectId,
		redirectOnSubmit: !!redirectUrl,
		selectedSubjob,
		setSubjobSearchText,
		subjobs,
		subjobSearchText,
		turnToProjectDisabled,
		workRequestId: jobId ? +jobId : undefined,
		initialized,
		billingCodesSyncErrors: syncErrors?.billingCodes ?? [],
		billingCodesSubmitErrors: billingCodesSubmitErrors ?? [],
		invoiceReminderRecipients,
		workLocation,
	}), [
		availableSubjobs,
		change,
		disableTurnToProject,
		goToJobForm,
		handleSubjobSelect,
		isProject,
		jobId,
		onTurnToProjectChange,
		openAddSubjobConfirmationModal,
		orgAlias,
		projectId,
		redirectUrl,
		selectedSubjob,
		subjobSearchText,
		subjobs,
		turnToProjectDisabled,
		initialized,
		syncErrors.billingCodes,
		billingCodesSubmitErrors,
		invoiceReminderRecipients,
		workLocation,
	]);

	if (!job) {
		return (
			<>
				<Breadcrumbs
					items={[
						{ label: 'Jobs', url: CLIENT.COMPANY.JOBS.TABLE(orgAlias, companyName) },
						{ label: 'Loading' },
						{ label: 'Edit' },
					]}
				/>
			</>
		);
	}

	return (
		<>
			<Breadcrumbs
				items={[
					{ label: 'Jobs', url: CLIENT.COMPANY.JOBS.TABLE(orgAlias, companyName) },
					{ label: job.jobCode },
					{ label: 'Edit' },
				]}
			/>
			<div className={styles['job-form__submit-section']}>
				<div className={styles['job-form__submit-section__hint']} />
				<Button
					label="Cancel"
					onClick={onBack}
					style="secondary"
				/>
				{
					hasCreatePermission &&
					<Button
						label="Copy"
						onClick={openCopyJobModal}
						style="secondary"
					/>
				}
				<SubmitButton
					disabled={!isFormValid}
					label="Save"
					onClick={handleSubmit(beforeEditJob)}
					reduxFormSubmitting={submitting}
				/>
			</div>
			<TabNavigation
				active={activeTabId}
				navigationClassName={styles['job-form__tabs-navigation']}
				onClick={onTabSelect}
				renderLabel={renderTabLabel}
				tabs={tabsAdjustedForPermissions}
			/>
			<Outlet context={context} />
			<CopyModal
				close={closeCopyJobModal}
				jobToCopyCode={job?.jobCode ?? ''}
				jobToCopyId={+jobId!}
				showModal={showCopyModal}
			/>
			<ConfirmationModal
				body="Changing this Job to External will make all associated Work Orders External and will be included in statistics."
				closeModal={closeConfirmationModal}
				confirmAction={handleSubmit(submit)}
				confirmText="Submit"
				modalStyle="danger"
				showModal={showSubmitConfirmationModal}
				title={'Submit Job?'}
			/>
			<ConfirmationModal
				body="Convert this Job to Project. This action is irreversible. Continue?"
				closeModal={closeTurnToProjectConfirmationModal}
				confirmAction={turnToProject}
				confirmText="Continue"
				modalStyle="grey-warning"
				showModal={showTurnToProjectConfirmationModal}
				title={'Convert Job to Project?'}
			/>
			<ConfirmationModal
				body={`Associate ${selectedSubjob?.jobCode} with ${jobCode}. Continue?`}
				closeModal={closeAddSubjobConfirmationModal}
				confirmAction={addSubjob}
				confirmText="Continue"
				modalStyle="grey-warning"
				showModal={showAddSubjobConfirmationModal}
				title={`Associate ${selectedSubjob?.jobCode} with ${jobCode}?`}
			/>
			<ConfirmationModal
				body={'You have associated Sub-jobs with this Project. After submiting the form Sub-jobs cannot be detached from the Project. Submit?'}
				closeModal={hideSubmitWithSubjobsConfirmationModal}
				confirmAction={handleSubmit(onEditJob)}
				confirmText="Submit"
				modalStyle="grey-warning"
				showModal={showSubmitWithSubjobsConfirmationModal}
				title={'Submit project with Sub-jobs?'}
			/>
			<ConfirmationModal
				body={renderBillingCodeChangesModalBody()}
				closeModal={closeBillingCodeChangesModal}
				confirmAction={confirmBillingCodeChanges}
				confirmText="Confirm"
				modalStyle="warning"
				showModal={showBillingCodeChangesModal}
				size={'lg'}
				title="Confirm Billing Codes Edits"
			/>
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;
	const formState = getFormValues(FORMS.JOB_EDIT);
	const formSelector = formValueSelector(FORMS.JOB_EDIT);
	const currentFormValues = getFormValues(FORMS.JOB_EDIT)(state) as JobUpsertFM;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const {
		jobHazardAssessmentStatus,
		isProject,
		subjobs,
		id: workRequestId,
		projectId,
		uploadedAttachmentIds,
		jobCode,
		title,
		customerCompanyName,
		priority,
		office,
		billToEmail,
		invoiceReminderRecipients,
		workLocation,
	} = formState(state) as JobUpsertFM ?? ({} as Partial<JobUpsertFM>);

	const getSubmitErrors = getFormSubmitErrors(FORMS.JOB_EDIT);

	const { jobCode: jobCodeSubmitError, billingCodes: billingCodesSubmitErrors } = getSubmitErrors(state) as FormErrors<JobUpsertFM, string>;
	const syncErrors = getFormSyncErrors(FORMS.JOB_EDIT)(state) as FormErrorsWithArray<JobUpsertFM, string>;

	/** DB_DATE_ONLY "YYYY-MM-DD" */
	const dueDate: string = formSelector(state, 'startDate');

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const hasPermissionsToManageBillingCodes = isAllowed(
		PagePermissions.COMPANY.JOBS.MANAGE_BILLING_CODES,
		permissions,
		isCompanyAdmin,
		role
	);

	const hasCreatePermission = isAllowed(PagePermissions.COMPANY.JOBS.CREATE, companyData.permissions, companyData.isCompanyAdmin, userData.role);

	return {
		companyName: companyData.name,
		jobHazardAssessmentStatus,
		formJobCode: jobCode,
		title,
		customerCompanyName,
		priority,
		office,
		isProject,
		subjobs,
		workRequestId,
		projectId,
		dueDate,
		currentFormValues,
		uploadedAttachmentIds,
		jobCode,
		billToEmail,
		jobCodeSubmitError,
		hasPermissionsToManageBillingCodes,
		syncErrors,
		billingCodesSubmitErrors: billingCodesSubmitErrors as unknown as FormErrorsWithArray<JobUpsertFM['billingCodes'][0], string>[],
		hasCreatePermission,
		invoiceReminderRecipients,
		workLocation,
	};
}

function mapDispatchToProps() {
	return {
		getJobForm: JobActions.getJobForm,
		editJob: JobActions.editJob,
		findAllAvailableSubjobs: WorkRequestActions.findAllAvailableSubjobs,
		deletePendingAttachments: AttachmentActions.deletePendingAttachments,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass>(
	reduxForm<JobUpsertFM, FormOwnProps>({
		form: FORMS.JOB_EDIT,
		validate: JobUpsertFM.validate,
		warn: JobUpsertFM.warn,
		shouldError: () => true, // show errors even when switching tabs
	}),
	connector
);

export default enhance(EditJob);
